import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, EkgDivider } from "@/components/Dividers";
import { Send, Linkedin, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socials = [
  { label: "LinkedIn", handle: "/in/tanvi-mandapaka", icon: Linkedin, href: "https://linkedin.com/in/tanvi-mandapaka-720b902b0" },
  { label: "Email", handle: "srit.mand@gmail.com", icon: Mail, href: "mailto:srit.mand@gmail.com" },
];

const fieldClass = "field-line mt-3 w-full bg-transparent border-b border-white/20 pb-4 font-display italic text-2xl md:text-3xl placeholder:text-white/25";

const Field = ({ id, label, testId, as = "input", ...props }) => {
  const Tag = as;
  return (
    <div>
      <label htmlFor={id} className="font-tech text-[10px] tracking-[0.35em] uppercase text-white/40">{label}</label>
      <Tag id={id} data-testid={testId} required className={`${fieldClass} ${as === "textarea" ? "resize-none" : ""}`} {...props} />
    </div>
  );
};

const SocialsCard = () => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-8 md:p-10" data-testid="contact-socials-card">
    <div className="flex flex-col items-center text-center gap-6">
      <div
        data-testid="profile-photo"
        className="relative w-48 h-48 md:w-56 md:h-56 shrink-0 rounded-full p-[4px] bg-[conic-gradient(from_180deg,#FF1493,#FF66B2,#00FFFF,#FF1493)] shadow-[0_0_60px_-10px_rgba(255,20,147,0.75)]"
      >
        <img src="/media/tanvi-headshot.jpg" alt="Tanvi Mandapaka" className="w-full h-full rounded-full object-cover border-[3px] border-[#050505]" />
      </div>
      <div>
        <p className="font-tech text-[10px] tracking-[0.35em] uppercase text-white/40">Frequent Flyer</p>
        <p className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-3 text-white" data-testid="contact-profile-name">Tanvi Mandapaka</p>
      </div>
    </div>
    <div className="mt-8 space-y-2">
      {socials.map((s) => {
        const external = s.href.startsWith("http");
        return (
          <a
            key={s.label}
            href={s.href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            data-testid={`social-link-${s.label.toLowerCase()}`}
            className="group flex items-center justify-between py-4 border-b border-white/10 transition-colors duration-300 hover:border-[#FF1493]/60"
          >
            <span className="flex items-center gap-4">
              <s.icon size={16} strokeWidth={1.5} className="text-[#FF1493]" />
              <span className="font-tech text-xs tracking-[0.25em] uppercase text-white/70 group-hover:text-white">{s.label}</span>
            </span>
            <span className="font-tech text-xs text-white/40 group-hover:text-[#FF66B2]">{s.handle}</span>
          </a>
        );
      })}
    </div>
  </div>
);

const EMPTY = { name: "", email: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received — cleared for takeoff.");
      setForm(EMPTY);
    } catch (err) {
      console.error("Contact submit failed", err);
      toast.error("Turbulence — the message didn't send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-40" data-testid="contact-section">
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="font-tech text-[11px] tracking-[0.4em] uppercase text-[#FF66B2]">Final Call</p>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-6">
            Gate open. <span className="italic text-[#FF1493]">Say hello.</span>
          </h2>
        </Reveal>
        <EkgDivider />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mt-8">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
              <Field id="contact-name" label="Passenger Name" testId="contact-name-input" value={form.name} onChange={update("name")} placeholder="Your Name Here" />
              <Field id="contact-email" label="Return Address" testId="contact-email-input" type="email" value={form.email} onChange={update("email")} placeholder="your.email@example.com" />
              <Field id="contact-message" label="Message in the Cabin" testId="contact-message-input" as="textarea" rows={3} value={form.message} onChange={update("message")} placeholder="Tell me about your lab, your startup, or your next destination..." />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-3 rounded-full bg-[#FF1493] text-black font-tech text-xs tracking-[0.25em] uppercase px-10 py-5 transition-transform duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
              >
                {sending ? "Taxiing..." : "Send it flying"}
                <Send size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <SocialsCard />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
